import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import "yup-phone-lite";
import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ErrorPage } from '../../components/ErrorPage/ErrorPage';
import { Button, Alert, Card, CardContent } from '../../components/primitives';
import { FormInput, FormSelect, type FormSelectOption } from '../../components/form';
import { AuthLayout } from '../AuthLayout';
import { OnboardingLayout } from '../OnboardingLayout';
import iso3166 from 'iso3166';
import { useSettings } from '../../hooks/useSettings';
import { SellerLocationSettingOption, ShippingCountryOfOriginOption } from '@shopify-multiseller/graphql/partner/types';
import { useConfig } from '../../hooks/useConfig';

export default function CompleteRegistration() {

  const auth = useAuth();
  const navigate = useNavigate();
  const settings = useSettings();
  const config = useConfig();

  // const sellerLocationSettings = settings.sellerLocation;
  // const hideCountrySelect = sellerLocationSettings.hideSelectCountryIfSingleCountry;
  // const option = sellerLocationSettings.sellerLocationOption;
  // const isSingleCountry = option === SellerLocationSettingOption.ShopCountryOnly;
  // const preselectedCountry = auth?.user?.seller?.address?.countryCode || "";

  interface CompleteRegistrationValues {
    firstName: string;
    lastName: string;
    companyName: string;
    phoneNumber: string;
    countryCode: string;
    terms: boolean;
  }

  const shopCountryCode = config?.shopConfig?.shop.countryCode;
  const sellerCountryCode = auth?.user?.seller?.address?.countryCode;
  // Prefill only. A self-serve registrant has no seller record yet (it is
  // created by THIS form), so there is often nothing to prefill — the
  // required, validated country select below is where the value comes from.
  // The old guard here treated a missing prefill as fatal: it setState'd
  // during render and returned undefined, blanking the screen for every
  // self-serve signup.
  const countryCode =
    (settings.productShipping.shippingCountryOfOriginOption === ShippingCountryOfOriginOption.ShopCountryOnly
      ? shopCountryCode
      : sellerCountryCode) ??
    (settings.sellerLocation.sellerLocationOption === SellerLocationSettingOption.ShopCountryOnly
      ? shopCountryCode
      : undefined) ??
    '';
  const [error, setError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const initialValues: CompleteRegistrationValues = {
    firstName: auth && auth.user?.firstName ? auth.user?.firstName : "",
    lastName: auth && auth.user?.firstName ? auth.user?.lastName : "",
    companyName: auth && auth.user?.companyName ? auth.user?.companyName : "",
    phoneNumber: auth && auth.user?.phone ? auth.user?.phone : "",
    countryCode: countryCode,
    terms: false
  };


  const completeRegistrationValidation = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    companyName: Yup.string().required("Company name is required"),
    phoneNumber: Yup.string()
      .phone("US", "Please enter a valid phone number")
      .required("A phone number is required"),
    countryCode: Yup.string().required("A country code is required"),
  });

  useEffect(() => {
    success && setTimeout(() => {
      navigate('/');
    }, 2000);
  }, [navigate, success]);

  const handleOnSubmit = async (
    values: CompleteRegistrationValues,
    actions: FormikHelpers<CompleteRegistrationValues>
  ) => {
    console.log("handling on submit");

    await auth?.completeRegistration({
      companyName: values.companyName,
      phone: values.phoneNumber,
      firstName: values.firstName,
      lastName: values.lastName,
      countryCode: values.countryCode
    }).then(() => {
      setSuccess(true);
      actions.setSubmitting(false);
    }).catch(err => {
      setError(
        "Could not complete your registration."
      );
      actions.setSubmitting(false);
    });
  };

  const handleOnChange = () => {
    setSubmitError(undefined);
  };

  const renderCountrySelect = (props: FormikProps<CompleteRegistrationValues>) => {
    const sellerLocationSettings = settings.sellerLocation;
    const hideCountrySelect = sellerLocationSettings.hideSelectCountryIfSingleCountry;
    const option = sellerLocationSettings.sellerLocationOption;
    const allowedCountries = sellerLocationSettings.allowedCountries;
    let isSingleCountry = false;

    let countryOptions: FormSelectOption[] = Object.entries(iso3166).map(([key, value]) => ({
      label: value.name,
      value: key
    }));

    const preselectedCountry = auth?.user?.seller?.address?.countryCode || "";

    switch (option) {
      case SellerLocationSettingOption.CustomList:
        countryOptions = countryOptions.filter(country => allowedCountries.includes(country.value));
        break;
      case SellerLocationSettingOption.ShopCountryOnly:
        countryOptions = countryOptions.filter(country => country.value === shopCountryCode);
        isSingleCountry = true;
        break;
      case SellerLocationSettingOption.AllCountries:
        countryOptions = countryOptions;
        break;
    }

    if (hideCountrySelect && isSingleCountry) {
      return (
        <input
          type="hidden"
          name="countryCode"
          value={preselectedCountry}
        />
      );
    }

    return (
      <FormSelect
        name="countryCode"
        label="Country"
        help="Where you ship from. Sets your default shipping origin."
        options={countryOptions}
        disabled={props.isSubmitting}
      />
    );
  }

  if (error) {
    return (
      <ErrorPage title={"Registration Error"} message={error} link={{ label: "Sign In", url: "/login" }} />
    );
  }

  if (success) {
    return <AuthLayout title="Registration Complete">Your registration is complete.</AuthLayout>;
  }

  return (
    <OnboardingLayout
      step="details"
      title="Tell us who's selling"
      subtitle="Shown to the marketplace team — customers only ever see your store name."
      banner={
        submitError && (
          <Alert variant="destructive">
            {submitError}
          </Alert>
        )
      }
    >
      <Card>
        <CardContent className="p-6">
      <Formik
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        validationSchema={completeRegistrationValidation}
      >
        {(props: FormikProps<CompleteRegistrationValues>) => (
          <Form className="space-y-4" onChange={handleOnChange}>
            {/* Two short fields on one line, as drawn — stacking them made the
                form a column of five identical full-width boxes. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput
                name="firstName"
                id="firstName"
                label="First name"
                required
                disabled={props.isSubmitting}
                data-testid="registration-first-name"
              />
              <FormInput
                name="lastName"
                id="lastName"
                label="Last name"
                required
                disabled={props.isSubmitting}
                data-testid="registration-last-name"
              />
            </div>
            <FormInput
              name="companyName"
              id="companyName"
              label="Company name"
              disabled={props.isSubmitting}
              data-testid="registration-company-name"
            />
            <FormInput
              name="phoneNumber"
              id="phoneNumber"
              label="Phone number"
              placeholder="+1 (555) 000-0000"
              disabled={props.isSubmitting}
              data-testid="registration-phone-number"
            />
            {renderCountrySelect(props)}
            <Button
              type="submit"
              className="w-full"
              disabled={props.isSubmitting}
              data-testid="registration-complete-button"
            >
              {props.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Continue to terms
            </Button>
          </Form>
        )}
      </Formik>
        </CardContent>
      </Card>
    </OnboardingLayout>
  );
}
